// Formatting in the owner's time zone (from /api/me), never the browser's: a viewer abroad
// still sees the owner's local times. Labels follow the phone app's Format.kt.

import type { EntryDTO } from "../../shared/dto.js";
import { roundHalfUp } from "../../shared/journal.js";
import { sourceLabel } from "../../shared/labels.js";

/** Whether this browser's locale writes times as 13:00 rather than 1:00 PM. */
export const prefers24h = (() => {
  const cycle = new Intl.DateTimeFormat(undefined, { hour: "numeric" }).resolvedOptions().hourCycle;
  return cycle === "h23" || cycle === "h24";
})();

/** "−1h", "0", "+2h": hours from an entry, on the moment view's axis. */
export function relativeHourLabel(hours: number): string {
  if (hours === 0) return "0";
  return hours > 0 ? `+${hours}h` : `−${-hours}h`;
}

/** "12am 4am … 8pm" or "0 4 … 24", as on the phone's axis. */
export function clockHourLabel(hour: number, use24h = prefers24h): string {
  if (use24h) return String(hour);
  if (hour === 0 || hour === 24) return "12am";
  if (hour === 12) return "12pm";
  return hour < 12 ? `${hour}am` : `${hour - 12}pm`;
}

const timeFormats = new Map<string, Intl.DateTimeFormat>();

export function formatTime(ms: number, zone: string, use24h = prefers24h): string {
  const key = `${zone}|${use24h}`;
  let f = timeFormats.get(key);
  if (!f) {
    f = new Intl.DateTimeFormat(undefined, { timeZone: zone, hour: "numeric", minute: "2-digit", hourCycle: use24h ? "h23" : "h12" });
    timeFormats.set(key, f);
  }
  return f.format(ms);
}

/** A time, marked "~7:10 PM" when it's only approximate. */
export function approxTime(ms: number, approx: number | null, zone: string): string {
  return approx === null ? formatTime(ms, zone) : `~${formatTime(ms, zone)}`;
}

/** An entry's time: "7:10 PM", or "~7:10 PM" when it's approximate. */
export function entryClock(entry: EntryDTO, zone: string): string {
  return approxTime(entry.t, entry.approx, zone);
}

/** "7:10 PM", "~7:10 PM", or "5:30 PM – 6:15 PM · Garmin" for activities. */
export function entryTimeText(entry: EntryDTO, zone: string): string {
  if (entry.kind !== "activity") return entryClock(entry, zone);
  const range = `${entryClock(entry, zone)} – ${formatTime(entry.endT, zone)}`;
  return entry.run ? `${range} · ${sourceLabel(entry.run.source)}` : range;
}

/** "±15 min" for an approximate time, null for an exact one. */
export function approxSpan(approx: number | null): string | null {
  return approx === null ? null : `±${duration(approx)}`;
}

/** Today's date in `zone`, "YYYY-MM-DD". */
export function todayIn(zone: string, nowMs = Date.now()): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone: zone, year: "numeric", month: "2-digit", day: "2-digit" }).format(nowMs);
}

/** Calendar arithmetic on "YYYY-MM-DD", with no time zone involved. */
export function addDays(date: string, days: number): string {
  const [y, m, d] = date.split("-").map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10);
}

export function isDateString(value: string | undefined): value is string {
  return !!value && /^\d{4}-\d{2}-\d{2}$/.test(value) && addDays(value, 0) === value;
}

/** "Today", "Yesterday" or "Tue, Sep 22". */
export function dayTitle(date: string, today: string): string {
  if (date === today) return "Today";
  if (date === addDays(today, -1)) return "Yesterday";
  return longDate(date, { weekday: "short", month: "short", day: "numeric", year: date.slice(0, 4) === today.slice(0, 4) ? undefined : "numeric" });
}

export function longDate(date: string, options: Intl.DateTimeFormatOptions): string {
  const [y, m, d] = date.split("-").map(Number) as [number, number, number];
  return new Intl.DateTimeFormat(undefined, { ...options, timeZone: "UTC" }).format(Date.UTC(y, m - 1, d, 12));
}

/** "+5" or "−5" (with a real minus sign). */
export function delta(mgdl: number): string {
  return mgdl >= 0 ? `+${mgdl}` : `−${-mgdl}`;
}

/** Nightscout's trend names as arrows. */
export function arrow(direction: string | null): string {
  switch (direction) {
    case "DoubleUp":
      return "⇈";
    case "SingleUp":
      return "↑";
    case "FortyFiveUp":
      return "↗";
    case "Flat":
      return "→";
    case "FortyFiveDown":
      return "↘";
    case "SingleDown":
      return "↓";
    case "DoubleDown":
      return "⇊";
    default:
      return "";
  }
}

/** "just now", "12 min ago", "2 h 5 min ago". */
export function ago(ms: number, nowMs: number): string {
  const minutes = Math.max(0, Math.floor((nowMs - ms) / 60_000));
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} min ago`;
  return `${Math.floor(minutes / 60)} h ${minutes % 60} min ago`;
}

export function insulin(units: number): string {
  return `${roundHalfUp(units, 1).toFixed(1)} U`;
}

export function carbs(grams: number): string {
  return `${Math.round(grams)} g`;
}

export function percent(value: number | null): string {
  return value === null ? "–" : `${Math.round(value)}%`;
}

/** Like `percent`, but a small nonzero share reads "<1%" rather than "0%". */
export function sharePercent(value: number | null): string {
  if (value === null) return "–";
  if (value > 0 && value < 0.5) return "<1%";
  return `${Math.round(value)}%`;
}

export function mgdl(value: number | null): string {
  return value === null ? "–" : String(Math.round(value));
}

/** A change in percentage points: "+3.2 pts", "−0.4 pts". */
export function points(delta: number): string {
  const v = Math.abs(delta) < 0.05 ? 0 : delta;
  return `${v >= 0 ? "+" : "−"}${Math.abs(v).toFixed(1)} pts`;
}

/** A signed change with a unit: "+12 mg/dL", "−0.3%". */
export function signed(delta: number, digits = 0, unit = ""): string {
  const rounded = Number(Math.abs(delta).toFixed(digits));
  const sign = rounded === 0 ? "±" : delta > 0 ? "+" : "−";
  return `${sign}${rounded.toFixed(digits)}${unit}`;
}

/** GMI in % with its IFCC value: "6.6% · 49 mmol/mol". */
export function gmi(value: number | null): string {
  if (value === null) return "–";
  return `${value.toFixed(1)}%`;
}

export function gmiIfcc(value: number | null): string {
  return value === null ? "" : `${Math.round((value - 2.15) * 10.929)} mmol/mol`;
}

/** "Sep 18 – Oct 1, 2026", or with both years when they differ. */
export function dateRange(from: string, to: string): string {
  const sameYear = from.slice(0, 4) === to.slice(0, 4);
  const start = longDate(from, { month: "short", day: "numeric", year: sameYear ? undefined : "numeric" });
  const end = longDate(to, { month: "short", day: "numeric", year: "numeric" });
  return from === to ? end : `${start} – ${end}`;
}

/** "45 min", "1 h 20 min" */
export function duration(minutes: number): string {
  const m = Math.round(minutes);
  if (m < 60) return `${m} min`;
  return m % 60 === 0 ? `${Math.floor(m / 60)} h` : `${Math.floor(m / 60)} h ${m % 60} min`;
}

/** Calendar days from `from` to `to` (both "YYYY-MM-DD"). */
export function daysBetween(from: string, to: string): number {
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000);
}

/** Monday = 0 … Sunday = 6, for calendar layouts. */
export function weekdayIndex(date: string): number {
  return (new Date(`${date}T12:00:00Z`).getUTCDay() + 6) % 7;
}

/** "just now", "5 min ago", "3 h ago", "4 days ago", then the date. */
export function relative(ms: number, nowMs: number, zone: string): string {
  const minutes = Math.floor((nowMs - ms) / 60_000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} min ago`;
  if (minutes < 24 * 60) return `${Math.floor(minutes / 60)} h ago`;
  if (minutes < 7 * 24 * 60) return `${Math.floor(minutes / (24 * 60))} days ago`;
  return shortDate(ms, zone);
}

/** "Sep 22, 2026" for an instant, in `zone`. */
export function shortDate(ms: number, zone: string): string {
  return longDate(todayIn(zone, ms), { month: "short", day: "numeric", year: "numeric" });
}

/** "at the time", "55 min after", "1 h 10 min before". */
export function relativeTo(ms: number): string {
  if (Math.abs(ms) < 60_000) return "at the time";
  return ms > 0 ? `${duration(ms / 60_000)} after` : `${duration(-ms / 60_000)} before`;
}
